import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Copy, CheckCircle2 } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

const DEFAULT_SUBJECT = "Outil de pilotage agence — Homly";
const DEFAULT_BODY = `Bonjour,

Je suis tombé(e) sur Homly, une plateforme française qui aide les agences immobilières à structurer le suivi vendeur et à valoriser la qualité de nos accompagnements. Je pense que ça mériterait qu'on y jette un œil ensemble.

En quelques mots :
• un cockpit de pilotage pour vous (suivi mandats, qualité, équipes)
• des profils professionnels vérifiés pour nous, avec avis vendeurs sourcés
• une page agence publique alimentée par notre travail réel
• compatible avec le CRM actuel, sans migration

Plus d'infos : https://homly.fr/pour-les-agences

Qu'en pensez-vous ?

Bonne journée,`;

/**
 * InviteAgencyModal — Permet à un agent d'inviter sa direction à découvrir
 * Homly via un email pré-rempli (mailto natif + copie texte).
 *
 * Décision UX (Iter167) : pas de partage social (WhatsApp/LinkedIn) pour
 * rester strictement B2B sérieux. Mailto = canal de référence d'une
 * communication interne agent → directeur.
 */
export const InviteAgencyModal = ({ isOpen, onClose }) => {
    const [subject, setSubject] = useState(DEFAULT_SUBJECT);
    const [body, setBody] = useState(DEFAULT_BODY);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setCopied(false);
            setSubject(DEFAULT_SUBJECT);
            setBody(DEFAULT_BODY);
        }
    }, [isOpen]);

    const handleSendEmail = () => {
        const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailto;
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(`${subject}\n\n${body}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch (e) {
            // Silent fail — fallback browsers without clipboard API
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="invite-agency-modal"
                >
                    <motion.div
                        initial={{ scale: 0.96, y: 16, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.96, y: 16, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0E0918] shadow-2xl"
                    >
                        {/* Header */}
                        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/8 bg-[#0E0918]/95 backdrop-blur">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-[#FF914D]/15 flex items-center justify-center">
                                    <Mail className="w-4.5 h-4.5 text-[#FF914D]" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-white" style={DISPLAY}>
                                        Inviter mon agence à découvrir Homly
                                    </h3>
                                    <p className="text-xs text-[#A19DAB] mt-0.5">
                                        Personnalisez le message, puis envoyez-le à votre direction.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                data-testid="invite-agency-close"
                                className="w-8 h-8 rounded-full flex items-center justify-center text-[#A19DAB] hover:text-white hover:bg-white/5 transition-colors"
                                aria-label="Fermer"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-[#A19DAB] mb-1.5 uppercase tracking-widest"
                                       style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                    Objet
                                </label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    data-testid="invite-agency-subject"
                                    className="w-full px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#A855F7]/40 focus:bg-white/[0.06] outline-none text-sm text-white transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-[#A19DAB] mb-1.5 uppercase tracking-widest"
                                       style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                    Message
                                </label>
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={11}
                                    data-testid="invite-agency-body"
                                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#A855F7]/40 focus:bg-white/[0.06] outline-none text-sm text-white/90 leading-relaxed resize-none transition-colors"
                                />
                            </div>
                        </div>

                        {/* Footer actions */}
                        <div className="sticky bottom-0 flex flex-col sm:flex-row gap-3 px-6 py-4 border-t border-white/8 bg-[#0E0918]/95 backdrop-blur">
                            <button
                                onClick={handleSendEmail}
                                data-testid="invite-agency-send"
                                className="group flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#681793] to-[#A855F7] text-white font-semibold text-sm shadow-lg shadow-[#A855F7]/25 hover:opacity-95 hover:scale-[1.01] transition-all"
                            >
                                <Mail className="w-4 h-4" />
                                Envoyer par email
                            </button>
                            <button
                                onClick={handleCopy}
                                data-testid="invite-agency-copy"
                                className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border text-sm font-medium transition-all ${
                                    copied
                                        ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300'
                                        : 'border-white/15 text-white/90 hover:border-white/30'
                                }`}
                            >
                                {copied ? (
                                    <>
                                        <CheckCircle2 className="w-4 h-4" /> Copié
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" /> Copier le message
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
